import React, { useEffect, useState } from "react"
import { FormikControl } from '@stem-sims/nexus';
import { Formik, Form as FormikForm, FormikConfig } from 'formik';
import moment from "moment"
import Form from 'react-bootstrap/Form'
import Row from "react-bootstrap/Row"
import Col from 'react-bootstrap/Col'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import InputGroup from "react-bootstrap/InputGroup"
import { StudentInfo, ProgramsType } from "../../../shared/students"
import * as Yup from 'yup'
import { studentNameAvailabilityCheck } from "../../../models/usernameAvailable";
import * as orgGroupModel from "../../../models/organizationGroups"
import * as orgModel from "../../../models/organizations"
import EditLicenses from "./EditLicenses"

interface StudentFormPrograms extends StudentInfo {
    programs: ProgramsType[]
}

interface Props {
    initialValues?: Partial<StudentFormPrograms>
    onSubmit: FormikConfig<StudentFormPrograms>["onSubmit"]
    /**
     * Label text for the submission button
     */
    submitLabel: string
    /**
     * Programs that have been used for a student can not be disabled, they must be archived separately
     */
     disabledPrograms?: ProgramsType[]
}
const defaultInitValues = {
    gender: "" as any,
    name: "",
    birthDay: "",
    id: "",
    ethnicity: "" as any,
    currentSchoolEnrollment: "" as any,
    iep: "" as any,
    retention: "",
    programs: [],
    instructors: [],
    englishLanguageLearner: "",
    group: "Default"
}
export default function StudentForm({ initialValues=defaultInitValues, onSubmit, submitLabel, disabledPrograms }: Props) {
    const [groups,setGroups] = useState<any[]>([])
    const [org,setOrg] = useState<any>({})
    const notAvailableTooltip = "To add this program, please purchase more licenses."
    const programAlreadyUsedTooltip = "To remove this program, please archive the license from the manage students page."

    useEffect(() => {
        orgModel.get().then((orgs)=>setOrg(orgs[0]))
        orgGroupModel.get().then((orgGroups) => {
            setGroups(orgGroups)
        })
    },[initialValues])

    return (<>
        <Modal.Header closeButton>
            <h2>{submitLabel} Student</h2>
        </Modal.Header>
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={
                Yup.object().shape({
                    name: Yup
                        //TODO: Check if two names are provided and the name DNE
                        .string()
                        .label("Student name")
                        .matches(/\w \w/, "Please enter both a first and last name.")
                        .test({
                            name: "Student Name",
                            message: "This student name is already taken.",
                            test: async name => {
                                if (name === initialValues?.name) {
                                    return true
                                }
                                return studentNameAvailabilityCheck(name)
                            }
                        })
                        .required("Name is required"),
                    birthDay: Yup
                        .date()
                        .nullable()
                        .label("Birth day")
                        .required("Birthday is required"),
                    programs: Yup
                        .array()
                        .min(1, "At least one program must be selected.")
                        .required(),
                })
            }
        >
            {({ isSubmitting, errors, touched }) => (
            <FormikForm>
                <Modal.Body className="g-3">
                <Row className="mb-2">
                    <Form.Label htmlFor="student-name" className="d-inline-block col-sm-3 text-sm-end">Name</Form.Label>
                    <Col sm={8} className='d-inline-block'>
                        <InputGroup className="">
                            <FormikControl
                                name="name"
                                placeholder="Name"
                                disabled={isSubmitting}
                                isInvalid={!!errors.name && touched.name}
                                aria-describedby="student-name-label"
                                groupText="First and Last"
                            />
                        </InputGroup>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Form.Label className="d-inline-block col-sm-3 text-sm-end">Birthday</Form.Label>
                    <Col sm={8} className="d-inline-block">
                        <InputGroup>
                            <FormikControl
                                type="date"
                                name="birthDay"
                                isInvalid={!!errors.birthDay && touched.birthDay}
                                max={moment().format("YYYY-MM-DD")}
                                min={"1905-01-01"}
                            />
                        </InputGroup>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Form.Label className="d-inline-block col-sm-3 text-sm-end">Programs</Form.Label>
                    <Col sm={8} className="d-inline-block">
                        <InputGroup>
                            <EditLicenses disabledPrograms={disabledPrograms} checkedDisabledTooltip={programAlreadyUsedTooltip} uncheckedDisabledTooltip={notAvailableTooltip}/>
                        </InputGroup>
                        {!!errors.programs && touched.programs &&
                            <Form.Control.Feedback type="invalid" className="d-block">{errors.programs as any}</Form.Control.Feedback>
                        }
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Form.Label className="d-inline-block col-sm-3 text-sm-end">Gender</Form.Label>
                    <Col sm={8} className="d-inline-block">
                        <InputGroup>
                            <FormikControl as="select" id = "gender" name = "gender">
                                <option disabled value = "">Gender:</option>
                                <option value = "Male">Male</option>
                                <option value = "Female">Female</option>
                                <option value = "Other">Other</option>
                                <option value = "Decline to Answer">Decline to Answer</option>
                            </FormikControl>
                            <span className="input-group-text">optional</span>
                        </InputGroup>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Form.Label htmlFor="ethnicity" className="d-inline-block col-sm-3 text-sm-end">Ethnicity</Form.Label>
                    <Col sm={8} className="d-inline-block">
                        <InputGroup>
                            <FormikControl as="select" name = "ethnicity">
                                <option disabled value = "">Ethnicity:</option>
                                <option value = "White">White</option>
                                <option value = "Hispanic Origin">Hispanic Origin</option>
                                <option value = "Mideastern/Arab Descent">Mideastern/Arab Descent</option>
                                <option value = "Black">Black</option>
                                <option value = "Asian/Pacific Islander">Asian/Pacific Islander</option>
                                <option value = "South Asian">South Asian</option>
                                <option value = "American Indian/Alaskan Native">American Indian/Alaskan Native</option>
                                <option value = "Multi-Racial">Multi-Racial</option>
                                <option value = "Decline to Answer">Decline to Answer</option>
                            </FormikControl>
                            <span className="input-group-text">optional</span>
                        </InputGroup>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Form.Label htmlFor="enrollment" className="d-inline-block col-sm-3 text-sm-end">Current School Enrollment</Form.Label>
                    <Col sm={8} className="d-inline-block">
                        <InputGroup>
                            <FormikControl as="select" name = "currentSchoolEnrollment">
                                <option disabled value = "">Enrollment:</option>
                                <option value = "Public">Public</option>
                                <option value = "Private">Private</option>
                                <option value = "Cyber">Cyber</option>
                                <option value = "Homeschooling">Homeschooling</option>
                                <option value = "Not Applicable">Not Applicable</option>
                                <option value = "Decline to Answer">Decline to Answer</option>
                            </FormikControl>
                            <span className="input-group-text">optional</span>
                        </InputGroup>
                    </Col>
                </Row>

                <Row className="mb-2">
                    <Form.Label htmlFor="iep" className="d-inline-block col-sm-3 text-sm-end">IEP related to reading</Form.Label>
                    <Col sm={8} className="d-inline-block">
                        <InputGroup>
                            <FormikControl as="select" id = "iep" name = "iep">
                                <option disabled value = "">IEP:</option>
                                <option value = "Yes">Yes</option>
                                <option value = "No">No</option>
                                <option value = "Decline to Answer">Decline to Answer</option>
                            </FormikControl>
                            <span className="input-group-text">optional</span>
                        </InputGroup>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Form.Label htmlFor="retention" className="d-inline-block col-sm-3 text-sm-end">Retention</Form.Label>
                    <Col sm={8} className="d-inline-block">
                        <InputGroup>
                            <FormikControl as="select" id = "retention" name = "retention">
                                <option disabled value = "">Retention:</option>
                                <option value = "Never Retained">Never Retained</option>
                                <option value = "Retained 1 Year">Retained 1 Year</option>
                                <option value = "Retained 2 Year">Retained 2 Year</option>
                                <option value = "Retained 3 Year">Retained 3 Year</option>
                                <option value = "Decline to Answer">Decline to Answer</option>
                            </FormikControl>
                            <span className="input-group-text">optional</span>
                        </InputGroup>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Form.Label htmlFor="english-language-learner" className="d-inline-block col-sm-3 text-sm-end">English Language Learner</Form.Label>
                    <Col sm={8} className="d-inline-block">
                        <InputGroup>
                            <FormikControl as="select" id="english-language-learner" name="english-language-learner">
                                <option disabled value = "">English Language Learner:</option>
                                <option value = "Yes">Yes</option>
                                <option value = "No">No</option>
                                <option value = "Decline to Answer">Decline to Answer</option>
                            </FormikControl>
                            <span className="input-group-text">optional</span>
                        </InputGroup>
                    </Col>
                </Row>
                
                {groups && groups.length > 0 && org?.personal === "No" && 
                    <Row className="mb-2">
                        <Form.Label className="d-inline-block col-sm-3 text-sm-end">Group</Form.Label>
                        <Col sm={8} className='d-inline-block'>
                            <InputGroup>
                                <FormikControl as="select" name="group" className="w-100">
                                    {groups.map((group) => <option value={group.Name} key={group.Name}>{group.Name}</option>)}
                                </FormikControl>
                            </InputGroup>
                        </Col>
                    </Row>
                }
                <Button variant="secondary" type="submit" size="lg" disabled={isSubmitting} className="d-block m-auto">
                    {submitLabel}
                </Button>
                </Modal.Body>
            </FormikForm>
            )}
        </Formik>
    </>)
}
